import { sortTestChannelByControlGroup } from '../tableUtils';
import { DISABLED_EVENTS } from '../Wizard/consts';
import {
    EVENT,
    UNIQUE_EVENT,
    REVENUE,
    SESSION,
    CONTROL_GROUP_RANGE,
    PercentageFormatter,
    USDollar,
    NA_VALUE,
    EVENT_REAL,
    UNIQUE_EVENT_REAL,
} from './consts';
import {
    calculateUpliftWithSignificance,
    formatDisplayDate,
    getIncrementality,
    mergeArrays,
    safeDivide,
    safeFormat,
} from './reportingUtilsHelper';

export const TRANSLATION_PREFIX_UPLIFT = 'STATIC.PAGES.AUDIENCES.AUDIENCE_AB_TESTING.REPORTING.UPLIFTS';

const createMetricData = () => ({
    count: 0,
    revenue: 0,
    uniqueCounts: 0,
});

const createNormalizedMetrics = (groupData, eventName, groupSize) => {
    const eventData = groupData?.[eventName];
    return {
        revenue: eventData?.revenue || 0,
        events: eventData?.count || 0,
        uniqueEvents: eventData?.uniqueCounts || 0,
        groupSize,
    };
};

export const getDatesInRange = (startDate, endDate) => {
    if (!startDate) return [];

    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate || new Date());

    [current, end].forEach(date => date.setHours(0, 0, 0, 0));

    while (current <= end) {
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        dates.push(`${year}-${month}-${day}`);
        current.setDate(current.getDate() + 1);
    }

    return dates;
};

const processEventData = (totalData, dateData, eventsData) => {
    if (!Array.isArray(eventsData)) return;

    eventsData.forEach(({ eventName, data }) => {
        if (!eventName || !data) return;

        totalData[eventName] = totalData[eventName] || createMetricData();

        Object.entries(data).forEach(([key, value]) => {
            if (typeof value === 'number' && key in totalData[eventName]) {
                totalData[eventName][key] += value;
            }
        });

        dateData[eventName] = { ...data };
    });
};

const processTestData = (totalData, dateData, testData) => {
    if (!testData) return;

    const {
        sessionCount = 0,
        uniqueSessionCount = 0,
        revenueCount = 0,
        revenueSum = 0,
        uniqueRevenueCount = 0,
    } = testData;

    totalData[SESSION] = totalData[SESSION] || createMetricData();
    totalData[REVENUE] = totalData[REVENUE] || createMetricData();

    totalData[SESSION].count += sessionCount;
    totalData[SESSION].uniqueCounts += uniqueSessionCount;
    totalData[REVENUE].count += revenueCount;
    totalData[REVENUE].revenue += revenueSum;
    totalData[REVENUE].uniqueCounts += uniqueRevenueCount;

    dateData[SESSION] = {
        count: sessionCount,
        revenue: 0,
        uniqueCounts: uniqueSessionCount,
    };

    dateData[REVENUE] = {
        count: revenueCount,
        revenue: revenueSum,
        uniqueCounts: uniqueRevenueCount,
    };
};

const calculateTotalsByEvent = (eventsDataFromStart, datesInRange, testChannels) => {
    const totals = {};
    const dates = {};
    const groupsSize = {};
    testChannels.forEach(channel => {
        if (!channel.maxRange) return;
        groupsSize[channel.maxRange] = 0;

        totals[channel.maxRange] = {
            [SESSION]: createMetricData(),
            [REVENUE]: createMetricData(),
        };
    });

    eventsDataFromStart.forEach(({ date, data }) => {
        if (!date || !Array.isArray(data)) return;

        dates[date] = dates[date] || {};

        data.forEach(testEntry => {
            const distributionRange = testEntry?.testDetails?.distributionRange;
            if (!distributionRange || !totals[distributionRange]) return;
            groupsSize[distributionRange] += testEntry?.testData?.totalUniqueDevices || 0;
            if (datesInRange.includes(date)) {
                dates[date][distributionRange] = dates[date][distributionRange] || {};
                processTestData(totals[distributionRange], dates[date][distributionRange], testEntry.testData);

                processEventData(totals[distributionRange], dates[date][distributionRange], testEntry.testEventsData);
            }
        });
    });

    return { totals, dates, groupsSize };
};

const normalizeTableData = (testChannels, eventName, dataTotals, groupsSize, translate) => {
    if (!testChannels?.length || !eventName || !dataTotals) return {};

    const controlGroup = dataTotals[CONTROL_GROUP_RANGE];
    const controlMetrics = createNormalizedMetrics(controlGroup, eventName, groupsSize[CONTROL_GROUP_RANGE]);

    return testChannels.reduce((result, channel) => {
        const channelData = dataTotals[channel.maxRange];
        if (!channelData) return result;

        const channelMetrics = createNormalizedMetrics(channelData, eventName, groupsSize[channel.maxRange]);
        const eventData = channelData[eventName] || {};
        const groupSize = groupsSize[channel.maxRange];

        result[channel.id] = {
            adnetworkName: channel.adnetworkName,
            adnetworkLogoPath: channel.adnetworkLogoPath,
            adnetworkDisplayName: channel.adnetworkDisplayName,
            isControlGroup: channel.isControlGroup,
            groupSize,
            eventCount: String(eventData.count || 0),
            engagementRate: safeFormat(PercentageFormatter, safeDivide(eventData.count, groupSize)),
            engagementIncrementality: getIncrementality(
                channel,
                channelMetrics,
                controlMetrics,
                eventName,
                'events',
                translate
            ),
            uniqueEventCount: String(eventData.uniqueCounts || 0),
            uniqueConversionRate: safeFormat(PercentageFormatter, safeDivide(eventData.uniqueCounts, groupSize)),
            uniqueIncrementality: getIncrementality(
                channel,
                channelMetrics,
                controlMetrics,
                eventName,
                'uniqueEvents',
                translate
            ),
            incrementalityRevenue: getIncrementality(
                channel,
                channelMetrics,
                controlMetrics,
                eventName,
                'revenue',
                translate
            ),
            totalRevenue: safeFormat(USDollar, eventData.revenue),
        };

        return result;
    }, {});
};

const normalizeChartData = (testChannels, calculatedByDates, eventName, segmentSize, datesInRange) => {
    if (!testChannels?.length || !eventName) return { dates: [] };

    const result = {
        dates: [],
        ...testChannels.reduce((acc, channel) => {
            acc[channel.maxRange] = {
                [EVENT]: [],
                [UNIQUE_EVENT]: [],
                [EVENT_REAL]: [],
                [UNIQUE_EVENT_REAL]: [],
            };
            return acc;
        }, {}),
    };

    datesInRange.forEach(date => {
        result.dates.push(formatDisplayDate(date));

        testChannels.forEach(channel => {
            const dateData = calculatedByDates[date];
            const metrics = dateData?.[channel.maxRange]?.[eventName];

            if (metrics) {
                const normalizer = segmentSize * (channel.maxRange - channel.minRange);
                const normalizedCount = safeDivide(metrics.count, normalizer);
                const normalizedUniqueCount = safeDivide(metrics.uniqueCounts, normalizer);

                result[channel.maxRange][EVENT].push(normalizedCount);
                result[channel.maxRange][UNIQUE_EVENT].push(normalizedUniqueCount);
                result[channel.maxRange][EVENT_REAL].push(metrics.count || 0);
                result[channel.maxRange][UNIQUE_EVENT_REAL].push(metrics.uniqueCounts || 0);
            } else {
                [EVENT, UNIQUE_EVENT, EVENT_REAL, UNIQUE_EVENT_REAL].forEach(key => {
                    result[channel.maxRange][key].push(0);
                });
            }
        });
    });

    return result;
};

const createUpliftEntry = (channel, translate, eventName, config) => {
    const { type, metrics, controlMetrics, isEmpty, tooltip } = config;
    const hasRevenue = metrics.revenue;
    const uplifts = [];

    const metricTypeMap = {
        UNIQUE_UPLIFT: 'conversion',
        REVENUE_UPLIFT: 'revenue',
        EVENT_UPLIFT: 'count',
        UPLIFT: 'count',
    };

    const createUplift = (labelKey, metricValue, controlValue) => {
        if (isEmpty) {
            return {
                label: translate(`${TRANSLATION_PREFIX_UPLIFT}.${labelKey}`),
                tooltip,
                value: NA_VALUE,
                isSignificant: false,
            };
        }

        const metricType = metricTypeMap[labelKey] || 'auto';

        const result = calculateUpliftWithSignificance(
            metricValue,
            controlValue,
            metrics.groupSize ?? 0,
            controlMetrics.groupSize ?? 0,
            metricType
        );

        return {
            label: translate(`${TRANSLATION_PREFIX_UPLIFT}.${labelKey}`),
            tooltip,
            value: result.uplift,
            isSignificant: result.isSignificant,
        };
    };

    const isEventKind = type === EVENT;

    const mainLabel = isEventKind ? (hasRevenue ? 'EVENT_UPLIFT' : 'UPLIFT') : 'UNIQUE_UPLIFT';

    const mainMetric = isEventKind ? 'events' : 'uniqueEvents';
    uplifts.push(createUplift(mainLabel, metrics[mainMetric], controlMetrics[mainMetric]));

    if (isEventKind && hasRevenue) {
        uplifts.push(createUplift('REVENUE_UPLIFT', metrics.revenue, controlMetrics.revenue));
    }

    return {
        uplifts,
        network: channel.adnetworkName,
        networkDisplayName: channel.adnetworkDisplayName,
        adnetworkLogoPath: channel.adnetworkLogoPath,
    };
};

const normalizeNetworkUplifts = (testChannels, translate, eventName, dataTotal, groupsSize, isEmpty = false) => {
    if (!testChannels?.length) return [];

    const controlMetrics = isEmpty
        ? {}
        : createNormalizedMetrics(dataTotal[CONTROL_GROUP_RANGE], eventName, groupsSize[CONTROL_GROUP_RANGE]);

    return testChannels
        .filter(channel => !channel.isControlGroup)
        .flatMap(channel => {
            const channelMetrics = isEmpty
                ? {}
                : createNormalizedMetrics(dataTotal[channel.maxRange], eventName, groupsSize[channel.maxRange]);

            return [
                createUpliftEntry(channel, translate, eventName, {
                    type: EVENT,
                    metrics: channelMetrics,
                    controlMetrics,
                    isEmpty,
                    tooltip: `${TRANSLATION_PREFIX_UPLIFT}.TOOLTIP`,
                }),
                createUpliftEntry(channel, translate, eventName, {
                    type: UNIQUE_EVENT,
                    metrics: channelMetrics,
                    controlMetrics,
                    isEmpty,
                    tooltip: `${TRANSLATION_PREFIX_UPLIFT}.UNIQUE_TOOLTIP`,
                }),
            ];
        });
};

export const normalizeReportData = (
    translate,
    testMetadata,
    startDate,
    endDate,
    reportDataByDate,
    selectedEventName
) => {
    const datesInRange = getDatesInRange(startDate, endDate);
    const datesFromStart = getDatesInRange(new Date(testMetadata.startDate), endDate);

    const testChannels = sortTestChannelByControlGroup(testMetadata?.testChannels || []);
    const relevantDatesData = (reportDataByDate || []).filter(({ date }) => datesInRange.includes(date));
    const relevantStartDatesData = (reportDataByDate || []).filter(({ date }) => datesFromStart.includes(date));

    const segmentSize = testMetadata.segment?.reach || 0;
    const events = mergeArrays(
        DISABLED_EVENTS,
        (testMetadata.events || []).map(event => ({ label: event, value: event }))
    );

    const baseResponse = {
        id: testMetadata.id,
        testChannels,
        testName: testMetadata.name || '',
        description: testMetadata.description || '',
        status: testMetadata.status || '',
        startDate: testMetadata.startDate || null,
        endDate: testMetadata.endDate || null,
        appsite: {
            label: testMetadata.appsite?.appName || '',
            value: testMetadata.appsite?.id || '',
            icon: testMetadata.appsite?.icon || '',
        },
        segment: {
            value: testMetadata.segment?.id || '',
            label: testMetadata.segment?.name || '',
            reach: segmentSize,
        },
        events,
        networkUplifts: normalizeNetworkUplifts(testChannels, translate, selectedEventName, {}, {}, true),
        groupsSize: {},
    };

    if (!relevantDatesData.length) {
        return baseResponse;
    }

    const { totals, dates: calculatedByDates, groupsSize } = calculateTotalsByEvent(
        relevantStartDatesData,
        datesInRange,
        testChannels
    );
    baseResponse.segment.reach = Object.values(groupsSize).reduce((a, b) => a + b, 0);
    const effectiveSegmentSize = segmentSize || baseResponse.segment.reach;

    return {
        ...baseResponse,
        groupsSize,
        networkUplifts: normalizeNetworkUplifts(testChannels, translate, selectedEventName, totals, groupsSize),
        tableData: normalizeTableData(testChannels, selectedEventName, totals, groupsSize, translate),
        chartData: normalizeChartData(
            testChannels,
            calculatedByDates,
            selectedEventName,
            effectiveSegmentSize,
            datesInRange
        ),
    };
};
